import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "./ui/card";
import { Search, Loader, Shield, DollarSign, AlertCircle } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import API_CONFIG from "../lib/ApiConfig";
import starHand from "../assets/images/star-hand.svg";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";

const SearchBuyersScreen = ({
  userData,
  onBuyersFound,
  qualifiedUser = true,
}) => {
  const { getIdToken } = useAuth();
  const [isSearching, setIsSearching] = useState(true);
  const [progress, setProgress] = useState(0);
  const [searchStage, setSearchStage] = useState(0);
  const [buyerFound, setBuyerFound] = useState(false);
  const [buyerData, setBuyerData] = useState(null);
  const [secondaryBuyerData, setSecondaryBuyerData] = useState(null);
  const [isAADirectFirst, setIsAADirectFirst] = useState(false);
  const [error, setError] = useState(null);
  const { trackButtonClick } = useInteractionTracking();

  useEffect(() => {
    analyticsService.trackPageView("buyer_search", {});
  }, []);

  const searchStartedRef = useRef(false);

  const searchStages = [
    "Analyzing your insurance profile...",
    "Finding interested buyers...",
    "Negotiating best offers...",
    "Almost there...",
  ];

  // Separate useEffect for progress animation
  useEffect(() => {
    let progressInterval;

    if (isSearching) {
      progressInterval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev + (95 - prev) * 0.1;
          return newProgress >= 95 ? 95 : newProgress;
        });
      }, 400);
    }

    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [isSearching]);

  // Separate useEffect for updating search stage based on progress
  useEffect(() => {
    if (
      progress > (searchStage + 1) * 25 &&
      searchStage < searchStages.length - 1
    ) {
      setSearchStage((prev) => prev + 1);
    }
  }, [progress, searchStage, searchStages.length]);

  // Main search effect - only runs once
  useEffect(() => {
    if (searchStartedRef.current) return;
    searchStartedRef.current = true;

    const fetchOffers = async () => {
      try {
        const token = await getIdToken();
        if (!token) throw new Error("Authentication required");

        // FIRST: Check if any rewards already exist
        const rewardsUrl = `${API_CONFIG.BASE_URL}/api/users/insurance-profile/rewards`;
        const rewardsResponse = await fetch(rewardsUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
        });

        if (rewardsResponse.ok) {
          const rewards = await rewardsResponse.json();
          if (rewards && rewards.length > 0) {
            setProgress(100);
            setIsSearching(false);
            setBuyerFound(true);

            const primaryReward = rewards[0];
            const buyerDataObj = {
              id: primaryReward.id,
              dataBuyerId: primaryReward.buyer?.id,
              name: primaryReward.buyer?.name,
              logo: primaryReward.buyer?.logo,
              listLogo: primaryReward.buyer?.listLogo,
              hasExistingReward: true,
              offer: {
                amount: primaryReward.amount,
                currency: "USD",
              },
              description: primaryReward.buyer?.description,
              shortDescription: primaryReward.buyer?.shortDescription,
              shopNowLink: primaryReward.buyer?.shopNowLink,
              allRewards: rewards,
              timestamp: new Date().toISOString(),
            };

            setBuyerData(buyerDataObj);
            analyticsService.trackPageView("existing_reward_found", {
              buyer: primaryReward.buyer?.name,
            });

            setTimeout(() => {
              if (onBuyersFound) {
                onBuyersFound(buyerDataObj);
              }
            }, 100);

            return;
          }
        }

        // Fetch offers from the API
        //"x-user-id": "CUST_jbvHfKTkTYkG"

        const offersUrl = `${API_CONFIG.BASE_URL}/api/users/insurance-profile/offers`;
        const response = await fetch(offersUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch offers: ${response.status}`);
        }

        const offers = await response.json();

        setProgress(100);
        setIsSearching(false);

        if (offers && offers.length > 0) {
          setBuyerFound(true);

          // Check if first buyer is AADirect
          const firstBuyer = offers[0];
          const isAADirect =
            firstBuyer.buyer.name?.toLowerCase() === "aadirect";
          setIsAADirectFirst(isAADirect);

          // Format the first buyer data
          const primaryBuyerData = {
            id: firstBuyer.id,
            dataBuyerId: firstBuyer.buyer.id,
            name: firstBuyer.buyer.name,
            logo: firstBuyer.buyer.logo,
            listLogo: firstBuyer.buyer.listLogo,
            offer: {
              amount: firstBuyer.amount,
              currency: "USD",
            },
            description: firstBuyer.buyer.description,
            shortDescription: firstBuyer.buyer.shortDescription,
            shopNowLink: firstBuyer.buyer?.shopNowLink,
            timestamp: new Date().toISOString(),
          };

          setBuyerData(primaryBuyerData);

          // If AADirect is first and there's a second buyer, store it
          if (isAADirect && offers.length > 1) {
            const secondBuyer = offers[1];
            const secondaryBuyerDataObj = {
              id: secondBuyer.id,
              dataBuyerId: secondBuyer.buyer.id,
              name: secondBuyer.buyer.name,
              logo: secondBuyer.buyer.logo,
              listLogo: secondBuyer.buyer.listLogo,
              offer: {
                amount: secondBuyer.amount,
                currency: "USD",
              },
              description: secondBuyer.buyer.description,
              shortDescription: secondBuyer.buyer.shortDescription,
              shopNowLink: secondBuyer.buyer?.shopNowLink,
              timestamp: new Date().toISOString(),
            };
            setSecondaryBuyerData(secondaryBuyerDataObj);
          }

          analyticsService.trackPageView("buyer_found", {
            buyer: primaryBuyerData.name,
            isAADirect: isAADirect,
          });
        } else {
          setBuyerFound(false);
          setTimeout(() => {
            if (onBuyersFound) {
              onBuyersFound(null);
            }
          }, 1000);
        }
      } catch (err) {
        console.error("Error fetching offers:", err);
        setError(err.message);
        setIsSearching(false);
        setProgress(100);
        setBuyerFound(false);

        setTimeout(() => {
          if (onBuyersFound) {
            onBuyersFound(null);
          }
        }, 1000);
      }
    };

    setTimeout(fetchOffers, 500);
  }, [getIdToken, onBuyersFound]);

  const handleAADirectSelection = () => {
    trackButtonClick("select-aadirect", {
      step_name: "buyer_search",
    });
    if (onBuyersFound && buyerData) {
      onBuyersFound(buyerData);
    }
  };

  const handleSecondaryBuyerSelection = () => {
    trackButtonClick("select-secondary-buyer", {
      step_name: "buyer_search",
      buyer_name: secondaryBuyerData?.name,
    });
    if (onBuyersFound && secondaryBuyerData) {
      onBuyersFound(secondaryBuyerData);
    }
  };

  const handleSingleBuyerSelection = () => {
    trackButtonClick("see-the-offer", {
      step_name: "buyer_search",
    });
    if (onBuyersFound && buyerData) {
      onBuyersFound(buyerData);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-4 mt-2">
        {buyerFound ? (
          <div className="w-20 h-20 bg-violet-200 rounded-full flex items-center justify-center mx-auto">
            <img className="w-12 h-12" src={starHand} alt="starHand" />
          </div>
        ) : null}

        <h1 className="text-xl font-bold uppercase !my-6">
          {isSearching
            ? "Looking for Buyers"
            : buyerFound
            ? isAADirectFirst
              ? "Great News! We Found Matches"
              : "Great News! A Buyer is Interested"
            : "Search Complete"}
        </h1>

        {isSearching ? (
          <>
            <p className="text-gray-600">This should only take a moment.</p>
            <div className="flex flex-col items-center justify-center p-8">
              <div className="flex items-center justify-center w-24 h-24 relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg
                    className="w-full h-full animate-spin"
                    viewBox="0 0 100 100"
                  >
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="#EAE7E7"
                      strokeWidth="13"
                      fill="none"
                    />
                    <path
                      d="M90,50 A40,40 0 0,0 50,10"
                      stroke="#7B61FF"
                      strokeWidth="13"
                      fill="none"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader className="w-8 h-8 text-[#7B61FF] animate-pulse" />
                </div>
              </div>
            </div>
          </>
        ) : null}

        {!isSearching && buyerFound && (
          <p className="text-gray-600">
            {isAADirectFirst ? (
              "Want personal help or prefer to handle it yourself?"
            ) : (
              <>
                <span
                  className="text-lg"
                  style={{ color: "#7B61FF", fontWeight: 700 }}
                >
                  {buyerData?.name || "[BUYER NAME]"}
                </span>
                {
                  " wants to make an offer for your insurance profile. Your reward is now pending while we finish the validation process"
                }
              </>
            )}
          </p>
        )}
      </div>

      {/* AADirect Multi-Buyer Selection */}
      {buyerFound && isAADirectFirst && secondaryBuyerData && (
        <div className="space-y-4">
          {/* AADirect Option */}
          <Card className="border-2 border-gray-200 hover:border-[#7B61FF] transition-colors cursor-pointer">
            <CardContent className="pt-6 pb-6">
              <div className="flex flex-col items-center text-center space-y-4">
                {buyerData.logo && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <img
                      src={buyerData.logo}
                      alt={buyerData.name}
                      className="h-16 w-auto object-contain"
                    />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-lg mb-2">
                    Talk to an expert at {buyerData.name}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    They'll get your Ownli Insurance Profile and can help right
                    away.
                  </p>
                </div>
                <button
                  onClick={handleAADirectSelection}
                  className="w-full uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors"
                >
                  See the offer from {buyerData.name}
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Secondary Buyer Option (e.g., Insurify) */}
          <Card className="border-2 border-gray-200 hover:border-[#7B61FF] transition-colors cursor-pointer">
            <CardContent className="pt-6 pb-6">
              <div className="flex flex-col items-center text-center space-y-4">
                {secondaryBuyerData.logo && (
                  <div className="bg-gray-400 p-4 rounded-lg">
                    <img
                      src={secondaryBuyerData.logo}
                      alt={secondaryBuyerData.name}
                      className="h-16 w-auto object-contain"
                    />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-lg mb-2">
                    Continue online with {secondaryBuyerData.name}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Review and complete your quote yourself.
                  </p>
                </div>
                <button
                  onClick={handleSecondaryBuyerSelection}
                  className="w-full uppercase font-bold bg-white text-[#7B61FF] border-2 border-[#7B61FF] px-4 py-3 rounded-[10px] hover:bg-gray-50 transition-colors"
                >
                  See the offer from {secondaryBuyerData.name}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Single Buyer Selection (Original Flow) */}
      {buyerFound && !isAADirectFirst && (
        <button
          onClick={handleSingleBuyerSelection}
          className="w-full flex items-center justify-center gap-2 !mt-9 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors"
        >
          <span>see the offer</span>
        </button>
      )}

      {/* Error Display */}
      {error && (
        <Card className="border-red-100 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-700">Something went wrong</p>
                <p className="text-sm text-red-600">
                  {error}. We'll try again later.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchBuyersScreen;
